/**
 * THE CARRY: a question's context, from another of the console's documents to
 * the Assistant page.
 *
 * The Assistant is a page of the SOC route, "/#assistant" (owner decision 21,
 * 2026-10-17). Processes, Devices and VPN are documents of their own, so
 * "Ask the assistant" on a device, a VPN device or a process there loads the
 * SOC route: the press leaves one record in this tab's sessionStorage, the SOC
 * route takes it once, and the page opens with the About chip (the subject and
 * its framing), the question to ask, and "← Back to <page> · <drill>" naming
 * the page and the drill the press came from (handover.ts drillAddress).
 *
 * "Open in Assistant" from a drill's chat carries THAT CHAT's conversation too
 * (the owner, 2026-10-17 ~21:45 EAT: the page opened empty, without what had
 * just been asked in the drill): its id and its questions and answers, which
 * continue on the page. Only the drill chat's own turns, stamped with the
 * customer they were asked under — never another conversation of this tab's,
 * and dropped whole with the record on any mismatch below.
 *
 * (Before decision 21 this carried the dock's draft and conversation across a
 * rail press beside the full-screen dock. The dock is gone, and with it every
 * press that left a record; the rules below are the ones that press was held
 * to, kept.)
 *
 * WHAT KEEPS IT FROM BEING ANYONE ELSE'S:
 *  - only to the SOC route ("/"), never to /api/* — and a press on Sign out
 *    (/api/logout) removes any record;
 *  - only for the document the press named, within a minute;
 *  - only for the same customer: the press stamps THIS tab's own selection
 *    (null when it has none: the remembered claim in localStorage is every
 *    tab's, so it is never the stamp), and the page applies the record only
 *    once its own tenant hydration has settled and names the same customer —
 *    a provider who picks another customer in a second tab meanwhile gets the
 *    page with no subject, and the record is dropped (AssistantChatProvider);
 *  - only for the account signed in at the press (whoami), and on the next
 *    page only for that same signed-in account (whoami, checked by the
 *    caller) and, where the plane mints one per session (the engine's CSRF
 *    cookie), the same session;
 *  - "← Back to" goes only to one of the console's own pages, on this origin;
 *  - only a record well formed throughout: anything else is dropped whole,
 *    never half-applied.
 * Storage that cannot be used carries nothing.
 */
import { readCookie } from "../../lib/api";
import type { AssistantSurface } from "./api";
import { parseDrillConversation, parseSubjectRef, type AssistantSubjectRef, type DrillConversation } from "./handover";

export const CARRY_KEY = "assistant.carry";
export const CARRY_MS = 60_000;

/** Where the Assistant page lives: the only document a carry goes to. */
export const ASSISTANT_DOCUMENT = "/";

/** The console's own pages: the only documents "← Back to" returns to. */
export const CONSOLE_DOCUMENTS: ReadonlySet<string> = new Set(["/", "/choke", "/devices", "/vpn", "/fleet"]);

/** Every surface the assistant knows: a carried surface outside it is a malformed record. */
const SURFACES: Record<AssistantSurface, true> = {
  "alert-drill": true,
  "process-action": true,
  graph: true,
  "kpi-drill": true,
  "choke-process": true,
  "choke-assurance": true,
  devices: true,
  web: true,
  "devices-assurance": true,
  behaviour: true,
  firewall: true,
  vpn: true,
  exposure: true,
  "devices-traffic": true,
  "vpn-traffic": true,
  playbooks: true,
  "policy-changes": true
};

/** The page a question was asked on: what "← Back to" names, and where it goes. */
export interface CarryBack {
  label: string;
  /** A same-origin address on one of CONSOLE_DOCUMENTS. */
  href: string;
}

export interface Carry {
  at: number;
  /** The pathname of the console page the press opens: the Assistant's, "/". */
  to: string;
  /** This tab's own selection at the press (selectedTenantNow), or null where it had none. */
  tenant: string | null;
  /** The signed-in account at the press (whoami). */
  user: string;
  /** The engine's per-session CSRF cookie at the press; "" where the plane sets none. */
  session: string;
  /** The subject: an exec id, "device:<mac>", "vpn:<peer>", "web:<client>", "change:<id>". */
  execId?: string;
  scopeLabel?: string;
  surface?: AssistantSurface;
  /** A question to ask on arrival. */
  question?: string;
  back?: CarryBack;
  /** The drill's subject, typed: what the About chip names (handover.ts). */
  subject?: AssistantSubjectRef;
  /**
   * The drill chat's conversation, continued on the page. Its customer stamp
   * is the record's own (`tenant`): a conversation asked under another
   * customer is never left.
   */
  conversation?: DrillConversation;
}

/** The engine's session marker, minted per sign-in (api/auth.go); the control plane sets none. */
export function sessionNow(): string {
  return readCookie("csrf_token");
}

/** The account a whoami answer names, or "" where it names none. */
export function whoName(who: unknown): string {
  if (!who || typeof who !== "object") return "";
  const o = who as Record<string, unknown>;
  const name = typeof o.user === "string" && o.user ? o.user : typeof o.username === "string" ? o.username : "";
  return name;
}

export function leaveCarry(carry: Carry): void {
  try {
    window.sessionStorage.setItem(CARRY_KEY, JSON.stringify(carry));
  } catch {
    // Storage blocked or full: the page simply opens without the context.
  }
}

export function clearCarry(): void {
  try {
    window.sessionStorage.removeItem(CARRY_KEY);
  } catch {
    // Nothing could have been left either.
  }
}

/**
 * The record this navigation left, taken once (it leaves storage when read):
 * for this page and this session, recent and well formed throughout; otherwise
 * null. The account (whoami) and the customer (after the page's tenant
 * hydration) are the caller's to check before it applies anything.
 */
export function takeCarry(): Carry | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(CARRY_KEY);
    if (raw !== null) window.sessionStorage.removeItem(CARRY_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const carry = parseCarry(raw);
  if (!carry) return null;
  const age = Date.now() - carry.at;
  if (age < 0 || age > CARRY_MS) return null;
  if (carry.to !== window.location.pathname) return null;
  if (carry.session !== sessionNow()) return null;
  return carry;
}

const isString = (v: unknown): v is string => typeof v === "string";
const optionalString = (v: unknown): v is string | null | undefined => v === undefined || v === null || typeof v === "string";

/** A "← Back to" target read strictly: a label, and an address on one of the console's own pages here. */
export function parseBack(value: unknown): CarryBack | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (!isString(o.label) || !o.label.trim() || !isString(o.href)) return null;
  let url: URL;
  try {
    url = new URL(o.href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || !CONSOLE_DOCUMENTS.has(url.pathname)) return null;
  return { label: o.label, href: url.pathname + url.search + url.hash };
}

/** A carry read strictly: every field of the type it must be, or nothing at all. */
export function parseCarry(raw: string): Carry | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  if (typeof o.at !== "number" || !Number.isFinite(o.at)) return null;
  if (!isString(o.to) || o.to !== ASSISTANT_DOCUMENT) return null;
  if (!(o.tenant === null || isString(o.tenant))) return null;
  if (!isString(o.user) || !o.user) return null;
  if (!isString(o.session)) return null;
  if (!optionalString(o.execId) || !optionalString(o.scopeLabel) || !optionalString(o.question)) return null;
  const surface = o.surface;
  if (!(surface === undefined || surface === null || (isString(surface) && Object.prototype.hasOwnProperty.call(SURFACES, surface)))) return null;
  let back: CarryBack | null = null;
  if (o.back !== undefined && o.back !== null) {
    back = parseBack(o.back);
    if (!back) return null;
  }
  let subject: AssistantSubjectRef | null = null;
  if (o.subject !== undefined && o.subject !== null) {
    subject = parseSubjectRef(o.subject);
    if (!subject) return null;
  }
  let conversation: DrillConversation | null = null;
  if (o.conversation !== undefined && o.conversation !== null) {
    conversation = parseDrillConversation(o.conversation);
    // Malformed, or asked under another customer than the record names: the whole record goes.
    if (!conversation || conversation.tenant !== ((o.tenant as string | null) ?? null)) return null;
  }
  return {
    at: o.at,
    to: o.to,
    tenant: (o.tenant as string | null) ?? null,
    user: o.user,
    session: o.session,
    ...(isString(o.execId) && o.execId ? { execId: o.execId } : {}),
    ...(isString(o.scopeLabel) && o.scopeLabel ? { scopeLabel: o.scopeLabel } : {}),
    ...(isString(surface) ? { surface: surface as AssistantSurface } : {}),
    ...(isString(o.question) && o.question.trim() ? { question: o.question } : {}),
    ...(back ? { back } : {}),
    ...(subject ? { subject } : {}),
    ...(conversation ? { conversation } : {})
  };
}

/** What "← Back to" calls the plane page a question was asked on, by its document. */
export function documentLabel(pathname: string): string | null {
  if (pathname === "/choke") return "Processes";
  if (pathname === "/devices") return "Devices";
  if (pathname === "/vpn") return "VPN";
  if (pathname === "/fleet") return "Fleet";
  return null;
}
